/**
 * 카테고리 기반 정적 클러스터 레이아웃 헬퍼 — Master Graph 전용.
 *
 * 블럭 카탈로그(거의 불변) 시각화에 force 시뮬레이션이 매 렌더마다 수렴을 새로 돌리는 건
 * 비용 대비 효용이 낮다. 각 카테고리에 고정 중심을 부여하고, 노드의 초기 좌표를
 * 해당 중심 주변으로 미리 결정해 `forceX/forceY` 약한 앵커 + `forceCollide` 만으로
 * 겹침을 해소한다. 결과적으로:
 *  - 수렴이 빠르고 예측 가능
 *  - asOf/필터 변경 시에도 카테고리 배치가 흔들리지 않음
 *  - "마스터 그래프 구조 이해" 라는 사용 맥락에 부합
 *
 * 순수 함수로 유지해 유닛 테스트로 계약을 고정한다.
 */

/** 카테고리 중심 좌표 (원형 배치). */
export interface CategoryAnchor {
	readonly category: string
	readonly x: number
	readonly y: number
}

/**
 * 카테고리 목록을 받아 원형 앵커 좌표를 결정.
 *
 * 각 카테고리는 `2π/N` 등간격으로 `-π/2` (상단 12시) 기준에서 시계방향으로 배치된다.
 *
 * @param categories 카테고리 이름 배열 (순서 유지)
 * @param radius 원의 반지름 (픽셀)
 * @returns 각 카테고리별 `{x, y}` 중심 좌표 배열 (입력 순서 보존)
 */
export function computeCategoryAnchors(
	categories: ReadonlyArray<string>,
	radius: number
): ReadonlyArray<CategoryAnchor> {
	const n = categories.length
	if (n === 0) return []
	if (n === 1) return [{ category: categories[0] ?? "", x: 0, y: 0 }]
	return categories.map((category, i) => {
		const angle = (i / n) * Math.PI * 2 - Math.PI / 2
		return {
			category,
			x: Math.cos(angle) * radius,
			y: Math.sin(angle) * radius
		}
	})
}

/**
 * 카테고리 앵커 맵 빌드 — 노드 순회에서 O(1) 접근을 위해 Map 변환.
 */
export function buildAnchorMap(
	anchors: ReadonlyArray<CategoryAnchor>
): ReadonlyMap<string, { readonly x: number; readonly y: number }> {
	const map = new Map<string, { x: number; y: number }>()
	for (const a of anchors) map.set(a.category, { x: a.x, y: a.y })
	return map
}

/**
 * 노드의 초기 좌표를 해당 카테고리 중심 주변으로 분산 — 결정적 jitter(id 해시).
 *
 * 결정적 jitter 는 Math.random() 을 쓰지 않아 테스트에서 재현성이 보장된다.
 * 좌표는 중심 ± `jitter` 범위 내 균등 분포.
 *
 * @param anchors 카테고리 앵커 (없는 카테고리는 원점 fallback)
 * @param nodeId 노드 식별자 (jitter 해시 시드)
 * @param category 노드 카테고리
 * @param jitter 중심 반경 (픽셀)
 */
export function initialNodePosition(
	anchors: ReadonlyMap<string, { readonly x: number; readonly y: number }>,
	nodeId: string,
	category: string,
	jitter: number
): { readonly x: number; readonly y: number } {
	const center = anchors.get(category) ?? { x: 0, y: 0 }
	const h = hash32(nodeId)
	// 두 독립 축 — hash 상하 16bit 분리
	const ux = ((h & 0xffff) / 0xffff) * 2 - 1
	const uy = (((h >>> 16) & 0xffff) / 0xffff) * 2 - 1
	return {
		x: center.x + ux * jitter,
		y: center.y + uy * jitter
	}
}

/**
 * 결정적 32bit 정수 해시 (FNV-1a 32bit). 외부 의존 없이 재현 가능한 jitter 시드.
 * 암호학적 용도 아님 — 시각 분포 충분.
 */
function hash32(s: string): number {
	let h = 0x811c9dc5
	for (let i = 0; i < s.length; i++) {
		h ^= s.charCodeAt(i)
		h = Math.imul(h, 0x01000193)
	}
	return h >>> 0
}
